import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { MoneySchema, money, multiplyMoney, type Money } from '../money.ts';

/**
 * Consumable stock: what the company holds, and how it got there
 * (INV-01).
 *
 * `docs/features/22-inventory.md` is the design. The short version is
 * the part worth repeating here, because it is the decision everything
 * else follows from.
 *
 * **Stock is a ledger, and the quantity is its sum.** The obvious design
 * is a number on the catalog item, decremented when a job takes some,
 * and it is wrong for a reason that shows up the first week: a count
 * that disagrees with the shelf — and it will, because somebody drops a
 * box — is unrecoverable without the history. "We have 640" cannot be
 * argued with. "1,000 in on the 12th, 240 to Saguaro, 120 to Bisbee"
 * can.
 *
 * The same reasoning keeps a withdrawn bid rather than deleting it, and
 * refuses to delete a warranty visit report.
 *
 * **Nothing edits or deletes a movement.** A miscount is corrected by an
 * opposing `adjusted` movement carrying a reason, which is what makes
 * the shelf and the system reconcilable six months later by somebody who
 * was not there.
 *
 * **This is not equipment.** A machine is one thing with a serial number
 * that comes back; a connector is one of a thousand identical things
 * that does not. The two modules answer different questions and share
 * nothing but the word "stock" in casual speech.
 */
export const STOCK_MOVEMENT_KINDS = [
  'received',
  'issued',
  'returned',
  'adjusted',
  'wastage',
  'checkedOut',
  'broughtBack',
] as const;
export const StockMovementKindSchema = z.enum(STOCK_MOVEMENT_KINDS);
export type StockMovementKind = z.infer<typeof StockMovementKindSchema>;

export const STOCK_MOVEMENT_LABEL: Record<StockMovementKind, string> = {
  received: 'Received',
  issued: 'Issued to a job',
  returned: 'Returned from a job',
  adjusted: 'Adjusted',
  wastage: 'Wastage',
  checkedOut: 'Checked out',
  broughtBack: 'Brought back',
};

/**
 * Five kinds, not two, and the extra three each earn their place.
 *
 * `issued` and `adjusted` are both "there is less than there was" and
 * are completely different facts: one is work done and billed, the other
 * is a count that was wrong. A ledger that merged them would make a
 * builder unable to tell a busy month from a leaky store.
 *
 * `wastage` is the one somebody will ask for after six months of not
 * having it: offcuts, breakages, the box that got rained on. Separating
 * it from `adjusted` is what turns "we keep losing connectors" from a
 * feeling into a number.
 *
 * `returned` exists because material comes back off a job, and recording
 * that as a negative issue would quietly reduce what the job was
 * charged without anybody deciding to.
 *
 * **`checkedOut` and `broughtBack` are custody, not consumption**
 * (INV2-07, SCRUM-269). The maintainer asked for a way to check stock
 * out "to a job site or whether they are keeping it with themselves in
 * their truck", and that is a different fact from `issued`: the stock
 * has left the shelf but it is **still the company's**, nothing is
 * charged, and it has to be able to come back.
 *
 * Modelled as its own pair of kinds rather than as an issue, for three
 * reasons that each bite:
 *
 * 1. If custody were an issue, stock on a job and stock in a van would
 *    be indistinguishable and "what is out with the crew" could not be
 *    answered at all.
 * 2. Bringing it back would need a negative issue, which would credit a
 *    job that was never charged.
 * 3. Checking out to a job site would become a second way to bill a job,
 *    competing with `issueStockToJob` — and two ways to bill is how a
 *    customer gets charged twice.
 *
 * **Billing still happens when the material is consumed**, which is
 * what `issueStockToJob` is for. Custody moves the box; the invoice
 * follows the work.
 */
export function movementIsIncrease(kind: StockMovementKind): boolean {
  return kind === 'received' || kind === 'returned' || kind === 'broughtBack';
}

/**
 * Whether this kind is custody rather than consumption (INV2-07).
 *
 * The one question the custody report asks of a movement, in one place
 * — a second `kind === 'checkedOut' || ...` somewhere else is how the
 * two lists come to disagree about what is out.
 */
export function movementIsCustody(kind: StockMovementKind): boolean {
  return kind === 'checkedOut' || kind === 'broughtBack';
}

export const StockMovementSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  /** The catalog item this is stock *of*. Stock has no identity apart from it. */
  catalogItemId: IdSchema,
  kind: StockMovementKindSchema,
  /**
   * Thousandths, like every quantity in this product, and **signed**:
   * an issue is negative.
   *
   * Signed rather than paired with the kind, so the on-hand quantity is
   * a sum and nothing has to remember which kinds subtract. A sum that
   * depends on a lookup table is a sum somebody will eventually get
   * wrong.
   *
   * `adjusted` is the only kind that may be either sign — a recount goes
   * both ways — and the only one that may be zero is none of them.
   */
  quantityThousandths: z.number().int(),
  /**
   * What this delivery cost per unit. **Only on `received`.**
   *
   * Kept whatever basis is used for charging a job, and that is
   * deliberate: it is the one fact that cannot be recovered later. The
   * maintainer chose the latest price paid (doc §4), and keeping each
   * delivery's real cost means moving to an average or to FIFO
   * afterwards is a computation over history rather than a migration
   * with missing data.
   */
  unitCost: MoneySchema.optional(),
  /** The day it happened, which is not always the day it was typed in. */
  on: DateOnlySchema,
  /** Set when it went to or came back from a job. */
  jobId: IdSchema.optional(),
  /**
   * Who has it, when it was checked out to a person (INV2-07).
   *
   * A member's uid. Custody is "this box is in Dave's van", which is a
   * fact about a person and not about a job — and the report that
   * answers "what is out with the crew" is built from exactly this.
   */
  custodyUid: IdSchema.optional(),
  /**
   * Where it went, when it was checked out to a place rather than a
   * person — a job site's container, another yard.
   */
  custodyLocationId: IdSchema.optional(),
  /**
   * The cost line this created on that job.
   *
   * Written in the same commit as the movement, so stock cannot fall
   * without the job being charged and a job cannot be charged for stock
   * that never left.
   */
  costLineId: IdSchema.optional(),
  /**
   * The delivery note or vendor invoice number.
   *
   * Carries weight beyond provenance: a received movement is identified
   * by its item and this, so re-importing the same spreadsheet counts
   * nothing twice. Double-counted stock is the one error here that is
   * genuinely hard to unpick.
   */
  reference: z.string().trim().max(120).optional(),
  /**
   * Why. Required on an adjustment, because a count that changed for no
   * recorded reason is the thing an auditor asks about and nobody can
   * answer.
   */
  note: z.string().trim().max(2_000).default(''),
  at: InstantSchema,
  by: IdSchema,
});
export type StockMovement = z.infer<typeof StockMovementSchema>;

/**
 * What is on hand, derived and written only by a trigger (INV-01).
 *
 * **Keyed by the catalog item id**, not an id of its own, so "what do we
 * have of this" is one read rather than a query. There is exactly one
 * level per item and it has no life apart from the item.
 *
 * Derived rather than stored, which this codebase has written down
 * twice already: two places that both claim to know a fact will
 * eventually disagree. `isScheduled` on a warranty claim is computed for
 * that reason and the equipment module refused a `current_job_id` field
 * for the same one. A client may read this and never write it; the rules
 * enforce that.
 *
 * **No money on it.** The maintainer chose the latest price paid, so the
 * cost of a unit is the catalog item's `unitCost` — there is no average
 * to maintain here and no second place for a price to live. Value at
 * cost is a multiplication a screen does, not a field that can go stale.
 */
export const InventoryLevelSchema = z.object({
  /**
   * The catalog item's id, which is also this document's id.
   *
   * Both fields hold it: `id` because every record in this product has
   * one and the data port requires it, `catalogItemId` because a reader
   * should not have to know that the two are the same to write a query.
   */
  id: IdSchema,
  catalogItemId: IdSchema,
  companyId: IdSchema,
  onHandThousandths: z.number().int(),
  /** So a screen can say how fresh the number is without reading the ledger. */
  lastMovementAt: InstantSchema.optional(),
  movementCount: z.number().int().min(0).default(0),
  updatedAt: InstantSchema,
});
export type InventoryLevel = z.infer<typeof InventoryLevelSchema>;

/** On hand, as a sum. The ledger is the truth and this is how it is read. */
export function onHandFrom(movements: readonly StockMovement[]): number {
  return movements.reduce((total, one) => total + one.quantityThousandths, 0);
}

/**
 * What stock is worth, at the latest price paid.
 *
 * **Named for the basis**, because the number does not reconcile to cash
 * spent and a reader is owed that. Buy 1,000 at 0.42 and 500 at 0.48 and
 * you paid 660.00 but hold 1,500 valued at 720.00. Every delivery's real
 * cost is on its own movement, so the difference is always explainable.
 */
export function valueAtLatestCost(onHandThousandths: number, unitCost: Money): Money {
  return multiplyMoney(unitCost, onHandThousandths / 1000);
}

/**
 * Whether taking this much would put the count below zero.
 *
 * **A warning, not a refusal**, and that is a judgement worth stating. A
 * store with less on the system than on the shelf is ordinary — a
 * delivery nobody typed in yet — and refusing to let a foreman take what
 * is physically in front of them would make the tool something people
 * work around. The screen says the count will go negative, and the
 * negative count is itself the signal that a delivery is missing.
 */
export function wouldGoNegative(onHandThousandths: number, takeThousandths: number): boolean {
  return onHandThousandths - takeThousandths < 0;
}

/** Quantities are thousandths; this is the one place that conversion lives. */
export function quantityFromDecimal(value: number): number {
  return Math.round(value * 1000);
}

export function decimalFromQuantity(thousandths: number): number {
  return thousandths / 1000;
}

/** Zero of the right currency, for a level with nothing in it. */
export function noValue(currency = 'USD'): Money {
  return money(0, currency);
}
